/**
 * The signed-in person's decision right delegations in the current workspace,
 * for the home's "Delegated to me" and "You delegated" panels (migration 980).
 * Reads my_decision_right_delegations through fetchMyDelegations; reloads when
 * the page is revisited (refreshKey) or after an act on a row (reload).
 */

import { useCallback, useEffect, useState } from 'react';
import { fetchMyDelegations, type MyDelegation } from '../../lib/decisionRightDelegations';

export interface MyDelegationsState {
  rows: MyDelegation[];
  loading: boolean;
  error: string | null;
  reload: () => void;
}

export function useMyDelegations(workspaceId: string | null, viewerId: string | null, refreshKey?: string): MyDelegationsState {
  const [rows, setRows] = useState<MyDelegation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!workspaceId || !viewerId) {
      setRows([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchMyDelegations(workspaceId)
      .then((next) => {
        if (cancelled) return;
        setRows(next);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not read your delegations.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId, viewerId, refreshKey, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { rows, loading, error, reload };
}
